import { z } from "zod";
import { useTRPC } from "../../../trpc";
import { Alert, Button, Group, ScrollArea, SimpleGrid, Stack, TextInput } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../../components/SimpleBreadcrumbs";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "@mantine/form";
import { useNavigate } from "@tanstack/react-router";
import { Link, LinkBack } from "../../../components/Link";
import { FormattedMessage, useIntl } from "react-intl";
import { IconExclamationCircle } from "@tabler/icons-react";
import { captureEvent } from "../../../utils/posthog";
import { Event } from "../../../constants/events";
import { KEY_RE, TileJoinOptionsZodSchema, TippecanoeOptionsZodSchema } from "@project/common";
import { SourcesInput } from "./components/SourcesInput";
import { LayersInput } from "./components/LayersInput";
import { TilesetJoinOptions } from "./components/TilesetJoinOptions";
import { cleanedObject, cleanOptions } from "./constants/tippecanoeFields";
import type { TilesetConfig, TileJoinOptions, TippecanoeOptions } from "@project/api";
import { useDefaultApiKey } from "../../../hooks/apiKey";
import { useMutation, useQuery } from "@tanstack/react-query";
import { usePathAccountName } from "../../../hooks/router";
import { API_HOST } from "../../../constants/url";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useMapkaNotification } from "../../../hooks/notifications.js";

export interface Source {
  id: string;
  name: string;
  file: File | null;
}

const makeDefaultSource = (): Source => ({
  id: crypto.randomUUID(),
  name: "",
  file: null,
});

export type LayerOptions = Partial<Omit<TippecanoeOptions, "layer">>;
export type JoinOptions = Partial<TileJoinOptions>;

export interface Layer {
  name: string;
  sources: string[];
  options: LayerOptions;
}
const makeDefaultLayer = (): Layer => ({
  name: "",
  sources: [],
  options: {},
});

interface TilesetForm {
  attribution?: string;
  description?: string;
  displayName: string;
  sources: Source[];
  layers: Layer[];
  joinOptions: JoinOptions;
}

export function NewTileset() {
  const intl = useIntl();
  const trpc = useTRPC();
  const mapkaNotification = useMapkaNotification();

  const navigate = useNavigate();
  const defaultApiKey = useDefaultApiKey();
  const accountName = usePathAccountName();
  const [error, setError] = useState<Error | null>(null);
  const [uploading, setSourceUploading] = useState(false);
  const { data: tilesets = [] } = useQuery(trpc.getTilesets.queryOptions());

  const { mutateAsync: createTileset, isPending } = useMutation(trpc.createTileset.mutationOptions());
  const { mutateAsync: createInitialVersion, isPending: isPendingVersion } = useMutation(trpc.createInitialVersion.mutationOptions());

  const validate = useMemo(() => zod4Resolver(
    z.object({
      sources: z
        .array(z.object({
          name: z
            .string()
            .regex(KEY_RE, {
              message: intl.formatMessage({
                id: "error.sourceNameInvalid",
                defaultMessage: "Source name must only contain alphanumeric characters, dashes, dots, or underscores.",
              }),
            })
            .min(1,
              intl.formatMessage({
                id: "error.sourceNameTooShort",
                defaultMessage: "Source name is too short",
              }),
            ),
          file: z
            .instanceof(File)
            .refine(({ size }) => size <= 10 * 1024 * 1024 * 1024,
              intl.formatMessage({
                id: "error.tooLarge",
                defaultMessage: "Tileset source is too large, max 10GB",
              }),
            ).refine(({ name }) => (
              name.endsWith(".zip")
              || name.endsWith(".geojson")
              || name.endsWith(".fgb")
              || name.endsWith(".mbtiles")
              || name.endsWith(".ldgeojson")
              || name.endsWith(".gpkg")
              || name.endsWith(".parquet")
              || name.endsWith(".kml")
              || name.endsWith(".gpx")
              || name.endsWith(".topojson")
              || name.endsWith(".gml")
            ),
            intl.formatMessage({
              id: "error.wrongFormat",
              defaultMessage: "Unsupported tileset source format",
            }),
            ),
        }))
        .min(1, {
          message: intl.formatMessage({
            id: "error.sourcesTooShort",
            defaultMessage: "Tileset must have at least one source",
          }),
        }),
      joinOptions: cleanedObject(TileJoinOptionsZodSchema.partial()),
      layers: z
        .array(z.object({
          name: z
            .string()
            .min(1,
              intl.formatMessage({
                id: "error.layerNameTooShort",
                defaultMessage: "Layer name is too short",
              }),
            ).max(255,
              intl.formatMessage({
                id: "error.layerNameTooLong",
                defaultMessage: "Layer name is too long",
              }),
            )
            .regex(KEY_RE, {
              message: intl.formatMessage({
                id: "error.layerNameInvalid",
                defaultMessage: "Name must only contain alphanumeric characters, dashes, dots, or underscores.",
              }),
            }),
          sources: z.array(z.string()).min(1, {
            message: intl.formatMessage({
              id: "error.layerSourcesEmpty",
              defaultMessage: "Layer must have at least one source",
            }),
          }),
          options: cleanedObject(TippecanoeOptionsZodSchema.omit({ layer: true })),
        }))
        .min(1, {
          message: intl.formatMessage({
            id: "error.layersTooShort",
            defaultMessage: "Tileset must have at least one layer",
          }),
        }),

      displayName: z.string()
        .min(1,
          intl.formatMessage({
            id: "error.tooShort",
            defaultMessage: "Tileset name is too short",
          }),
        ).max(255,
          intl.formatMessage({
            id: "error.tilesetTooLong",
            defaultMessage: "Tileset name is too long",
          }),
        )
        .refine(displayName => tilesets.every(tileset => tileset.displayName !== displayName),
          intl.formatMessage({
            id: "error.tilesetExists",
            defaultMessage: "Tileset with this name already exists",
          }),
        ),
    }),
  ), [tilesets]);

  const initialValues = useMemo(() => ({
    displayName: "",
    description: "",
    attribution: "",
    sources: [makeDefaultSource()],
    layers: [makeDefaultLayer()],
    joinOptions: {} as JoinOptions,
  } satisfies TilesetForm), []);

  const {
    values,
    onSubmit,
    setFieldValue,
    getInputProps,
    removeListItem,
    insertListItem,
  } = useForm({
    validateInputOnBlur: true,
    initialValues,
    validate,
  });

  const uploadSources = useCallback(async ({ tileset, sources }: { tileset: string; sources: Source[] }) => {
    const results: { formId: string; id: number; name: string }[] = [];
    for (const { id: formId, file, name } of sources) {
      if (!file) continue;

      const body = new FormData();

      const {
        lastModified,
      } = file;

      body.append("name", name);
      body.append("lastModifiedAt", new Date(lastModified).toISOString());
      body.append("file", file);

      const uploaded = await fetch(`https://${API_HOST}/v1/${accountName}/tilesets/${tileset}/sources`, {
        method: "POST",
        body,
        headers: {
          Authorization: `Bearer ${defaultApiKey}`,
        },
      }).then(async (res) => {
        if (!res.ok) {
          throw new Error("Failed to upload source");
        }
        return res.json() as Promise<{ id: number; name: string }>;
      });

      results.push({ formId, ...uploaded });
    }
    return results;
  }, [defaultApiKey, accountName]);

  const handleSubmit = useCallback(({
    displayName,
    sources,
    layers,
    attribution,
    description,
    joinOptions,
  }: TilesetForm) => {
    captureEvent(Event.TilesetCreateNewTileset);
    setError(null);
    setSourceUploading(true);

    createTileset({ displayName }).then(async ({
      key: tileset,
      id: tilesetId,
    }) => {
      return uploadSources({
        tileset,
        sources,
      }).then((newSources) => {
        const sourceIds = newSources.map(s => s.id);
        const idByFormId = new Map(newSources.map(s => [s.formId, s.id]));
        const configLayers = layers.map(({
          name,
          sources: layerSourceIds,
          options,
        }) => {
          const layerSources = layerSourceIds.map((formId) => {
            const sourceId = idByFormId.get(formId);
            if (sourceId === undefined) {
              throw new Error(`Source ${formId} not found`);
            }
            return {
              sourceId,
              type: "TilesetSource" as const,
            };
          });
          return {
            sources: layerSources,
            options: {
              ...cleanOptions(options),
              layer: name,
            },
          };
        });

        // tile-join is only meaningful with >1 layer; name falls back to the tileset display name
        const cleanedJoinOptions = cleanOptions(joinOptions);
        const join_options = layers.length > 1
          ? {
              ...cleanedJoinOptions,
              name: cleanedJoinOptions.name ?? displayName,
            }
          : undefined;

        const config: TilesetConfig = {
          attribution,
          description,
          ...(join_options ? { join_options } : {}),
          layers: configLayers,
        };

        void createInitialVersion({
          tilesetId,
          sourceIds,
          config,
          format: "mvt",
        }).then(() => {
          captureEvent(Event.TilesetCreateNewTilesetSuccess);
          void navigate({
            to: "/$accountName/tilesets/$tileset/overview",
            params: { tileset, accountName },
          });
          mapkaNotification({
            title: intl.formatMessage({
              defaultMessage: "New tileset created",
              id: "notification.newTilesetCreated",
            }),
            message: intl.formatMessage({
              defaultMessage: "Successfully created {tileset}",
              id: "notification.newTilesetCreatedMessage",
            }, { tileset }),
            variant: "success",
          });
        });
      });
    }).catch((err) => {
      if (err instanceof Error) {
        setError(err);
        logger.error({
          error: err,
          displayName,
        }, "Failed to create tileset");
      }
    }).finally(() => {
      setSourceUploading(false);
    });
  }, [intl, accountName, createTileset, uploadSources, setError, navigate, setSourceUploading]);

  const {
    sources,
    layers,
    displayName: formDisplayName,
    joinOptions: formJoinOptions,
  } = values as TilesetForm;

  const isLoading = isPending || uploading || isPendingVersion;

  const handleRemoveSource = useCallback((sourceIndex: number) => {
    captureEvent(Event.TilesetRemoveSource);
    const removedId = sources[sourceIndex]?.id;
    removeListItem("sources", sourceIndex);
    if (!removedId) return;

    layers.forEach(({ sources: layerSources }, layerIndex) => {
      if (layerSources.includes(removedId)) {
        setFieldValue(
          `layers.${layerIndex}.sources`,
          layerSources.filter(id => id !== removedId),
        );
      }
    });
  }, [sources, layers, removeListItem, setFieldValue]);

  const handleAddSource = useCallback(() => {
    captureEvent(Event.TilesetAddSource);
    insertListItem("sources", makeDefaultSource());
  }, [insertListItem]);

  const handleRemoveLayer = useCallback((index: number) => {
    captureEvent(Event.TilesetRemoveLayer);
    removeListItem("layers", index);
  }, [removeListItem]);

  const handleAddLayer = useCallback(() => {
    captureEvent(Event.TilesetAddLayer);
    insertListItem("layers", makeDefaultLayer());
  }, [insertListItem]);

  const label = intl.formatMessage({
    defaultMessage: "Tileset name",
    id: "label.tilesetName",
  });
  const descriptionLabel = intl.formatMessage({
    defaultMessage: "Description",
    id: "label.description",
  });
  const attributionLabel = intl.formatMessage({
    defaultMessage: "Attribution",
    id: "label.attribution",
  });

  return (
    <Stack px="md" my="md" h="100%">
      <SimpleBreadcrumbs
        parent={<LinkBack href="/app/"><FormattedMessage defaultMessage="Tilesets" id="navigation.tilesets" /></LinkBack>}
        current={intl.formatMessage({ defaultMessage: "New Tileset", id: "navigation.newTileset" })}
        description={intl.formatMessage({ defaultMessage: "Create new tileset, define config and integrations.", id: "text.newTilesetDescription" })}
      />
      {error && (
        <Alert variant="light" color="red" icon={<IconExclamationCircle />}>
          {error.message}
        </Alert>
      )}

      <ScrollArea
        type="always"
        offsetScrollbars
        mr="-8px"
        overscrollBehavior="auto"
      >
        <form onSubmit={onSubmit(handleSubmit)}>
          <Stack flex={1}>
            <TextInput
              label={label}
              disabled={isLoading}
              placeholder={label}
              required
              {...getInputProps("displayName")}
            />
            <TextInput
              label={descriptionLabel}
              disabled={isLoading}
              placeholder={descriptionLabel}
              {...getInputProps("description")}
            />
            <TextInput
              label={attributionLabel}
              disabled={isLoading}
              placeholder={attributionLabel}
              {...getInputProps("attribution")}
            />

            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="xs">
              <SourcesInput
                onAddSource={handleAddSource}
                onRemoveSource={handleRemoveSource}
                isLoading={isLoading}
                sources={sources}
                getInputProps={getInputProps}
              />

              <LayersInput
                onAddLayer={handleAddLayer}
                onRemoveLayer={handleRemoveLayer}
                isLoading={isLoading}
                sources={sources}
                layers={layers}
                getInputProps={getInputProps}
              />
            </SimpleGrid>

            {layers.length > 1 && (
              <TilesetJoinOptions
                isLoading={isLoading}
                getInputProps={getInputProps}
                layers={layers}
                displayName={formDisplayName}
                joinOptions={formJoinOptions}
              />
            )}
          </Stack>

          <Group gap="xs" justify="flex-end" pt="lg">
            <Link href="/app/tilesets">
              <Button variant="secondary" type="reset" loading={isLoading}>
                <FormattedMessage defaultMessage="Cancel" id="button.cancel" />
              </Button>
            </Link>

            <Button variant="tertiary" type="submit" loading={isLoading}>
              <FormattedMessage defaultMessage="Create" id="button.create" />
            </Button>
          </Group>
        </form>
      </ScrollArea>
    </Stack>
  );
}
